// ============================================================================
// Hooks para ingestão/chunking de páginas da KB (F2 Bloco 1/3)
// - useIngestKbPage: dispara Edge Function kb-ingest-page
// - useKbIngestionStatus: lista status de ingestão por página do tenant
// ============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export interface KbIngestionStatusRow {
  page_id: string;
  space_id: string;
  title: string;
  status: string;
  chunks_count: number;
  last_chunk_at: string | null;
  published_version_id: string | null;
}

export interface IngestKbPageResult {
  ok: boolean;
  page_id: string;
  version_id: string;
  chunks_generated: number;
  inserted: number;
  skipped: number;
  full_reingest: boolean;
}

/** Dispara chunking de uma página publicada via Edge Function. */
export function useIngestKbPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { pageId: string; fullReingest?: boolean }) => {
      const { data, error } = await supabase.functions.invoke<IngestKbPageResult>(
        "kb-ingest-page",
        { body: { page_id: params.pageId, full_reingest: params.fullReingest ?? false } },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error("Falha na ingestão da página");
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["kb", "ingestion-status"] });
      qc.invalidateQueries({ queryKey: ["kb", "page", vars.pageId] });
    },
    onError: (error) => {
      logger.error("[useIngestKbPage]", "erro na ingestão", error);
    },
  });
}

/** Lista status de ingestão (chunks por página) do tenant. */
export function useKbIngestionStatus(spaceId?: string, enabled = true) {
  return useQuery({
    queryKey: ["kb", "ingestion-status", spaceId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_ingestion_status", {
        p_space_id: spaceId ?? null,
      });
      if (error) throw error;
      return (data ?? []) as KbIngestionStatusRow[];
    },
    enabled,
    staleTime: 15_000,
  });
}

// ============================================================================
// F2 Bloco 2/3 — Embeddings
// ============================================================================

export interface EmbedKbChunksResult {
  ok: boolean;
  embedded: number;
  pending_returned: number;
  batches: number;
  total_tokens: number;
  latency_ms: number;
  errors: string[];
  config: { model: string; dimension: number; provider: string };
  index: unknown;
}

/** Dispara geração de embeddings para chunks pendentes do tenant (ou de uma página). */
export function useEmbedKbChunks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params?: {
      pageId?: string;
      limit?: number;
      batchSize?: number;
      maxBatches?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke<EmbedKbChunksResult>(
        "kb-embed-chunks",
        {
          body: {
            page_id: params?.pageId ?? null,
            limit: params?.limit,
            batch_size: params?.batchSize,
            max_batches: params?.maxBatches,
          },
        },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error("Falha ao gerar embeddings");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb", "ingestion-status"] });
      qc.invalidateQueries({ queryKey: ["kb", "embedding-config"] });
    },
    onError: (error) => {
      logger.error("[useEmbedKbChunks]", "erro na geração de embeddings", error);
    },
  });
}

/** Lê configuração ativa de embedding (para exibir modelo/dimensão na UI). */
export function useKbEmbeddingConfig(enabled = true) {
  return useQuery({
    queryKey: ["kb", "embedding-config"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_get_active_embedding_config");
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      return (row ?? null) as {
        profile_id: string;
        model_id: string;
        provider_id: string;
        model_slug: string;
        embedding_dimension: number;
        provider_slug: string;
        hnsw_min_chunks: number;
      } | null;
    },
    enabled,
    staleTime: 60_000,
  });
}
